import { createListenerMiddleware } from '@reduxjs/toolkit';

import { Toaster } from '~components/Toaster';
import type { TMessage } from '~entities/messages';
import {
	loadFromSessionStorage,
	saveToSessionStorage,
	ssKeys,
} from '~features/storage';
import { GTMAnalytics } from '~services/analytics';
import { authApi } from '~services/api/auth';
import { botsApi, processLastMessageText } from '~services/api/bots';
import {
	dialogsApi,
	messagesAdapter,
	messagesSelector,
} from '~services/api/dialogs';
import { generationApi } from '~services/api/generation';
import { userApi } from '~services/api/user';
import { apiTags } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';
import {
	eventsInbox,
	EventType,
	isMsgType,
} from '~shared/services/eventsInbox';
import {
	Level,
	Logger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';
import { messageReceived } from '~store/utils/message-received';
import {
	adaptAudioMessage,
	adaptSextingMessage,
	adaptVideoMessage,
	addEncodedTextFieldToMessage,
	addGifPath,
	formatIMGPaths,
} from '~utils/messages';

import type { AppDispatch, RootState } from '../index';

export const wsEventListener = createListenerMiddleware();

wsEventListener.startListening({
	matcher: authApi.endpoints.authenticate.matchFulfilled,
	effect: (action, listenerApi) => {
		if (typeof window === 'undefined') {
			return;
		}

		listenerApi.unsubscribe();

		eventsInbox.onMsg((msg) => {
			const { id: userId } = (listenerApi.getState() as RootState).auth.info;
			if (isMsgType(msg, EventType.LevelChanged)) {
				const data = msg.payload;
				const tag = data.changes.reason.messageTag;
				const elem = document.getElementById(tag);
				if (elem) {
					elem.dataset.exp = `+${data.changes.experienceChanged}`;
				}
				listenerApi.dispatch(
					userApi.util.updateQueryData('getLevel', { userId }, (draft) => {
						Object.assign(draft ?? {}, data.current);
					}) as AppDispatch
				);
				if (data.changes.levelChangedMessage) {
					Toaster.showLevelMessage({ text: data.changes.levelChangedMessage });
				}
			}
			let isExistingMsg = false;

			if (isMsgType(msg, EventType.ChatMessageReceived)) {
				const data = msg.payload;
				const encodedMessage = addEncodedTextFieldToMessage(data);
				const { id, text, meta, recipient, sender, timestamp, tag } =
					encodedMessage;
				const isOwn = sender === userId;
				const botId = isOwn ? recipient : sender;
				const lastMessage = processLastMessageText(text, meta);
				const oldMessageId = data.meta?.regeneration?.oldMessageId;
				const isSexting =
					data.meta?.ranking?.labels?.includes('sexting') || false;

				const notOwnSexting = isSexting && !isOwn;

				if (notOwnSexting) {
					GTMAnalytics.userDidReceiveSextingMessage(botId);
				}

				if (!isOwn) {
					listenerApi.dispatch(messageReceived(botId));
				}

				listenerApi.dispatch(
					dialogsApi.util.updateQueryData(
						'getMessages',
						{ id: userId, recipientId: botId, omit: 0 },
						(draft) => {
							const existingMsg = messagesSelector?.selectById(
								draft?.messages,
								data.tag
							);
							const replacedMessage = Object.values(
								messagesSelector.selectEntities(draft?.messages) ?? {}
							).find(
								(message): message is TMessage =>
									message !== undefined && message.id === oldMessageId
							);
							const newData = adaptVideoMessage(
								adaptSextingMessage(
									formatIMGPaths(addGifPath(adaptAudioMessage(data)))
								)
							);
							const currMessageId = existingMsg?.tag ?? replacedMessage?.tag;
							if (currMessageId) {
								isExistingMsg = true;
								messagesAdapter.updateOne(draft.messages, {
									id: currMessageId,
									changes: newData,
								});
							} else {
								messagesAdapter.upsertOne(draft?.messages, newData);
								draft.totalLength += 1;
							}
						}
					) as AppDispatch
				);
				const { isSafe } = (listenerApi.getState() as RootState).auth.info;
				listenerApi.dispatch(
					botsApi.util.updateQueryData('getContacts', { userId }, (draft) => {
						const bot = Object.values(draft.contacts ?? {}).find(
							({ clientId }) => clientId === +botId
						);
						// TODO: check for isSafe bot sender for SFW mode and update chat list accordingly.
						if (!bot) {
							return;
						}

						if (bot.visibility === 'default' && isSafe) {
							return;
						}

						bot.generations = bot.generations.filter((it) => it !== tag);

						// total initially updated as part of optimistic response in sendMessage
						// when multiple tabs opened sendMessage fires only on focused tab (obviously)
						// so for proper total counting for all tabs we initially check if message present in chat and toggle isExistingMsg bool
						bot.totalMessagesCount += isExistingMsg ? 0 : 1;
						bot.unreadMessagesCount += isOwn ? 0 : 1;

						bot.lastMessage = {
							id,
							timestamp,
							senderId: sender,
							recipientId: recipient,
							isSexting:
								notOwnSexting && !/(photo|audio|video)/.test(lastMessage),
							text: lastMessage,
							tarifficationType: meta?.tariffication?.type,
							paid: meta?.tariffication?.paid ?? false,
							cost: meta?.tariffication?.cost ?? 0,
						};
					}) as AppDispatch
				);
			}
			if (isMsgType(msg, EventType.ChatImageGenerationStarted)) {
				const { botId, tag } = msg.payload;
				listenerApi.dispatch(
					botsApi.util.updateQueryData('getContacts', { userId }, (draft) => {
						const bot = Object.values(draft.contacts).find(
							({ clientId }) => clientId === +botId
						);
						if (!bot) {
							return;
						}

						if (!bot.generations.includes(tag)) {
							bot.generations.push(tag);
						}
					}) as AppDispatch
				);
			}

			if (
				isMsgType(
					msg,
					EventType.ChatBecomeAvailable,
					EventType.ChatBecomeUnavailable
				)
			) {
				const {
					auth: {
						info: { id },
					},
					api,
				} = listenerApi.getState() as RootState;

				const botId = msg.payload.botId;
				const data: Record<string, unknown> =
					(api.queries?.[`getContacts-${id}`]?.data as Record<
						string,
						unknown
					>) ?? {};
				const dialogs = (data.contacts as Record<string, unknown>) ?? {};
				const isBotAccessible: boolean =
					dialogs.hasOwnProperty(botId) && !!dialogs[botId];

				// we may receive events for soft bots, but they're not accessible on web, skip logic
				if (!isBotAccessible) {
					return;
				}
				void listenerApi.dispatch(
					botsApi.endpoints.getBotsAvailability.initiate(id, {
						forceRefetch: true,
					})
				);
				void listenerApi.dispatch(
					generationApi.util.invalidateTags([
						apiTags.imagesGenerationBots,
						apiTags.videosGenerationBots,
					])
				);

				const hasMessages =
					api.queries[`getMessages-${msg.payload.botId}-${id}`];
				void listenerApi.dispatch(
					dialogsApi.endpoints.getMessages.initiate(
						{
							id,
							recipientId: msg.payload.botId,
							omit: 0,
						},
						{
							forceRefetch: !hasMessages, // with forceRefetch will create new cache instance (case when new resident opens by reaching level). But if we have instance already we dont need this (chat with DD)
						}
					)
				);
			}

			const isImageGenerated = isMsgType(msg, EventType.ImageGenerated);
			const isVideoGenerated = isMsgType(msg, EventType.VideoGenerated);

			if (isImageGenerated || isVideoGenerated) {
				const {
					api,
					auth: {
						info: { id },
					},
				} = listenerApi.getState() as RootState;

				const isQueryExists = api.queries[`getGeneratedMedia-${id}`];

				if (isImageGenerated) {
					Logger.shared.log(
						Level.info,
						LoggerServices.NetworkService,
						LoggerMessages.ImageGeneratedEventReceived,
						{
							payload: {
								userId: id,
								botId: msg.payload?.generated?.botId,
								generationId: msg.payload?.generated?.id,
								queryExists: isQueryExists,
							},
						}
					);
				}

				if (!isQueryExists) {
					return;
				}

				void listenerApi.dispatch(
					generationApi.util.updateQueryData(
						'getGeneratedMedia',
						{ userId: id, omit: NaN, select: NaN },
						(draft) => {
							const current = draft.media.find(
								(it) => it.id === msg.payload.generated.id
							);
							if (msg.payload.generated.status === 'generated') {
								if (isImageGenerated) {
									saveToSessionStorage(ssKeys.IMG_GEN_PREV_SOME_SUCCESS, true);
								} else if (isVideoGenerated) {
									saveToSessionStorage(
										ssKeys.VIDEO_GEN_PREV_SOME_SUCCESS,
										true
									);
								}
							}

							const mediaItem = generationApi.prepareMediaItem(
								msg.payload.generated
							);

							if (current) {
								Object.assign(current, mediaItem);
							} else {
								draft.media.unshift(mediaItem);
								draft.totalLength += 1;
							}
						}
					) as AppDispatch
				);
				const { type } = TokenManager.getInfo();

				const isError =
					msg.payload.generated.status !== 'processing' &&
					msg.payload.generated.status !== 'generated';
				if (
					type === 'demo' &&
					loadFromSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, false) &&
					isImageGenerated
				) {
					GTMAnalytics.createFreeImage(!isError);
				}
				if (isError) {
					listenerApi.dispatch(
						generationApi.util.invalidateTags([
							isImageGenerated
								? apiTags.imagesGenerationQuota
								: apiTags.videosGenerationQuota,
						])
					);
					Toaster.showError({
						title: `Failed to generate ${isImageGenerated ? 'image' : 'video'}`,
						text: 'We refunded your generation. Please try again',
					});
				} else if (isVideoGenerated) {
					Toaster.showSuccess({
						title: 'Congratulations!',
						text: 'Your video is ready! Check it out now.',
					});
				}
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, false);
			}
		});
	},
});
